import { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  FormField,
  Input,
  Button,
  Alert,
  Flashbar,
} from '@cloudscape-design/components';
import { processCalculatorStandalone } from '../services/s3Service';
import PricingCalculatorTab from '../components/PricingCalculatorTab';
import { isESCCalculatorUrl } from '../utils/escUtils';

// Mirrors the lambda's extract_calculator_id regex — validates before API call
// Both standard AWS (?id=<hex>) and ESC (?id=<hex>) use the same format
const CALCULATOR_ID_REGEX = /id=([a-f0-9]+)/;

const CalculatorCheck = () => {
  const location = useLocation();
  const history = useHistory();

  const [calculatorUrl, setCalculatorUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Pre-fill URL from query string — enables shareable links
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlParam = params.get('calculator_url');
    if (urlParam) {
      setCalculatorUrl(urlParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isValidUrl = (url) => CALCULATOR_ID_REGEX.test(url);

  const handleAnalyze = async () => {
    setError(null);
    setResult(null);

    if (!isValidUrl(calculatorUrl)) {
      setError('Invalid Pricing Calculator URL. Make sure it contains a valid calculator ID (e.g. ?id=abc123...). Supports both standard AWS and ESC (European Sovereign Cloud) calculators.');
      return;
    }

    // Persist URL in query string for shareability
    history.replace({ search: `?calculator_url=${encodeURIComponent(calculatorUrl)}` });

    try {
      setLoading(true);
      const data = await processCalculatorStandalone(calculatorUrl);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = ({ detail }) => {
    if (detail.key === 'Enter' && !loading && calculatorUrl.trim()) {
      handleAnalyze();
    }
  };

  return (
    <SpaceBetween size="l">
      {error && !loading && (
        <Flashbar
          items={[{
            type: 'error',
            header: 'Processing Failed',
            content: error,
            dismissible: true,
            onDismiss: () => setError(null),
            id: 'calc-error',
          }]}
        />
      )}

      <Container
        header={
          <Header
            variant="h1"
            description="Review any AWS Pricing Calculator independently, without linking it to a Fund Request. Results are not saved."
          >
            Standalone Calculator Review
          </Header>
        }
      >
        <SpaceBetween size="m">
          <FormField
            label="AWS Pricing Calculator URL"
            description="Paste the share URL from AWS Pricing Calculator (supports standard AWS and European Sovereign Cloud)"
          >
            <Input
              value={calculatorUrl}
              onChange={({ detail }) => setCalculatorUrl(detail.value)}
              placeholder="https://calculator.aws/pricing/2/estimate?id=... or https://pricing.calculator.aws.eu/..."
              disabled={loading}
              onKeyDown={handleKeyDown}
            />
          </FormField>

          <Box>
            <Button
              variant="primary"
              onClick={handleAnalyze}
              loading={loading}
              disabled={!calculatorUrl.trim() || loading}
            >
              {loading ? 'Processing...' : 'Analyze Calculator'}
            </Button>
          </Box>

          {loading && (
            <Alert type="info">
              Processing may take up to a minute while fetching live AWS pricing data.
            </Alert>
          )}
        </SpaceBetween>
      </Container>

      {result && (
        <PricingCalculatorTab
          request={{
            validation_data: {
              complete_api_data: {
                fundRequest: {
                  artifacts: { pricingCalculatorLinks: [result.calculatorUrl] }
                }
              }
            },
            approval_summary: {}
          }}
          preloadedData={result}
          isESC={isESCCalculatorUrl(result.calculatorUrl)}
        />
      )}
    </SpaceBetween>
  );
};

export default CalculatorCheck;
